import type { Option } from '@zen/DesignSystem/types';
import { cargoTypeLabelMapping, ModeOfTransport, VehicleTypeEnum } from '@zen/types';
import { formatNumber } from '@zen/utils/formatting';
import type { DeepNullable, Nullable, Optional } from '@zen/utils/typescript';

import { getLegacyCargoType, palletTypes, templatedPalletTypes } from './cargoDictionaryMapping.helper';
import type {
  CargoDimensionsInputType,
  CargoDimensionsType,
  CargoWeight,
  CargoWeightType,
  ContainerType,
  ContainerTypeEnum,
  CoreCargoType,
  Dimensions,
  FullContainerFormData,
  LooseCargoFormData,
  PalletTypeEnum,
  UpdateContainerCargoItemInput,
  UpdateLooseCargoItemInput,
  UpdateVehicleCargoItemInput,
  VehicleCargoFormData
} from './types';
import {
  CargoDimensionsUnitEnum,
  CargoItemTypeEnum,
  CargoModeEnum,
  cargoValues,
  CargoWeightUnitEnum,
  coreCargoContainerTypeLabelMapping,
  coreCargoTypeLabelMapping,
  LooseCargoTypeEnum,
  RiskLevelsEnum
} from './types';

export const getWeight = (weight: Optional<CargoWeightType | CargoWeight>): Nullable<CargoWeightType> => {
  if (!weight || !weight?.value) {
    return null;
  }

  const metric = (weight as CargoWeight).unit || (weight as CargoWeightType).metric;

  return {
    metric: metric as CargoWeightUnitEnum,
    value: weight.value
  };
};

export const getDimensions = (dimensions: Optional<CargoDimensionsType | Dimensions>): Nullable<CargoDimensionsInputType> => {
  if (!dimensions || !dimensions.length || !dimensions.width || !dimensions.height) {
    return null;
  }
  const metric = ((dimensions as Dimensions).unit || (dimensions as CargoDimensionsType).metric) as CargoDimensionsUnitEnum;

  return {
    height: dimensions.height,
    width: dimensions.width,
    length: dimensions.length,
    metric
  };
};

export const isCoreCargoPalletType = (cargoType: Optional<CoreCargoType>): boolean =>
  !!cargoType && palletTypes.includes(cargoType);

export const getLooseCargoLabel = (
  looseCargoType: LooseCargoTypeEnum,
  palletType: Nullable<PalletTypeEnum> | undefined = undefined
): string => {
  if (looseCargoType === LooseCargoTypeEnum.PALLETS) {
    return (palletType && cargoTypeLabelMapping[palletType]) || 'Pallets';
  }

  return cargoTypeLabelMapping[looseCargoType];
};

export const formatWeight = (weight: Optional<CargoWeightType | CargoWeight>): string => {
  if (!weight) {
    return '-';
  }

  const weightUnit: string = (weight as CargoWeightType).metric?.toLowerCase() || (weight as CargoWeight).unit?.toLowerCase();

  return weight?.value ? `${formatNumber(weight.value, 4)} ${weightUnit}` : '-';
};

export const formatTotalWeight = (weightArray: Optional<CargoWeightType[]>): string => {
  if (!weightArray || weightArray?.length < 1) {
    return '-';
  }

  return weightArray.map(formatWeight).join(', ');
};

export const formatDimensions = (dimensions: Optional<CargoDimensionsType | Dimensions>): string => {
  if (!dimensions) {
    return '-';
  }

  const { width, height, length } = dimensions;

  const unit = (dimensions as Dimensions).unit || (dimensions as CargoDimensionsType).metric;

  return `${length} x ${width} x ${height} ${unit?.toLowerCase()}`;
};

export type FullContainerCargoRequestPayload = Omit<UpdateContainerCargoItemInput, 'id'>;
export type VehicleCargoRequestPayload = Omit<UpdateVehicleCargoItemInput, 'id'>;
export type LooseCargoRequestPayload = Omit<UpdateLooseCargoItemInput, 'id'>;

export const prepareLooseCargoRequestPayload = (values: LooseCargoFormData): LooseCargoRequestPayload => {
  const {
    actualCbm,
    chargeableWeight,
    containerNumber,
    dimensions,
    grossWeight,
    hazardous,
    looseCargoType,
    cargoType,
    refrigerated,
    quantity,
    stackable,
    trailerId
  } = values;

  const payload = {
    actualCbm,
    chargeableWeight: getWeight(chargeableWeight as Nullable<CargoWeight>),
    containerNumber,
    dimensions: getDimensions(dimensions),
    grossWeight: getWeight(grossWeight as Nullable<CargoWeight>),
    hazardous: !!hazardous,
    looseCargoType: getLegacyCargoType(looseCargoType as CoreCargoType) as LooseCargoTypeEnum,
    reefer: !!refrigerated,
    quantity,
    stackable: !!stackable,
    ...(trailerId && { trailerId })
  };

  if (looseCargoType === cargoValues.loosePalletLegacy) {
    return {
      ...payload,
      palletType: getLegacyCargoType(cargoType) as PalletTypeEnum
    };
  }

  return payload;
};

export const prepareVehicleCargoRequestPayload = (values: VehicleCargoFormData): VehicleCargoRequestPayload => {
  const {
    actualCbm,
    dimensions,
    grossWeight,
    hazardous,
    healthCertificate,
    looseCargoType,
    refrigerated,
    riskLevel,
    quantity,
    tailLift,
    valueOfGoods,
    vehicleType,
    trailerId
  } = values;

  return {
    actualCbm,
    dimensions: getDimensions(dimensions),
    grossWeight: getWeight(grossWeight as Nullable<CargoWeight>),
    hazardous: !!hazardous,
    healthCertificate: !!healthCertificate,
    looseCargoType: getLegacyCargoType(looseCargoType as CoreCargoType) as LooseCargoTypeEnum,
    reefer: !!refrigerated,
    riskLevel,
    quantity,
    tailLift: !!tailLift,
    valueOfGoods: valueOfGoods?.value ? valueOfGoods : null,
    vehicleType: getLegacyCargoType(vehicleType as CoreCargoType) as VehicleTypeEnum,
    trailerId
  };
};

export const prepareContainerCargoRequestPayload = (values: FullContainerFormData): FullContainerCargoRequestPayload => {
  const {
    actualCbm,
    containerNumber,
    containerSealNumber,
    containerType,
    dryStandard,
    flatRack,
    garmentsOnHangers,
    grossWeight,
    hazardous,
    looseCargoType,
    openTop,
    overweight,
    quantity,
    refrigerated
  } = values;

  return {
    actualCbm,
    containerNumber,
    containerSealNumber,
    containerType: getLegacyCargoType(containerType) as ContainerTypeEnum,
    dryStandard: !!dryStandard,
    flatRack: !!flatRack,
    garmentsOnHangers: !!garmentsOnHangers,
    grossWeight: getWeight(grossWeight as Nullable<CargoWeight>),
    hazardous: !!hazardous,
    looseCargoType: looseCargoType ? (getLegacyCargoType(looseCargoType) as LooseCargoTypeEnum) : null,
    openTop: !!openTop,
    overweight: !!overweight,
    quantity,
    reefer: !!refrigerated
  };
};

export const cargoItemTypeLabelMapping: Record<CargoItemTypeEnum, string> = {
  [CargoItemTypeEnum.CONTAINER]: 'Full container load',
  [CargoItemTypeEnum.LOOSE_CARGO]: 'Loose',
  [CargoItemTypeEnum.VEHICLE]: 'Full truck load'
};

export const cargoModeLabelMapping: Record<CargoModeEnum, string> = {
  [CargoModeEnum.FCL]: 'Full container load',
  [CargoModeEnum.FTL]: 'Full truck load',
  [CargoModeEnum.LOOSE]: 'Loose'
};

export const cargoRiskLevelLabelMapping: Record<RiskLevelsEnum, { description: string; title: string }> = {
  [RiskLevelsEnum.LOW]: {
    title: 'Lowest risk',
    description: 'Will be shipped using equipment appropriate for the cargo according to your SLA'
  },
  [RiskLevelsEnum.MEDIUM]: {
    title: 'Theft attractive',
    description: 'Will be shipped with added safety measures according to your SLA'
  },
  [RiskLevelsEnum.HIGH]: {
    title: 'Highly theft attractive',
    description: 'Will be shipped with added safety measures according to your SLA'
  }
};

export const consolidatedCargoLabelMapping: Record<CargoModeEnum, string> = {
  [CargoModeEnum.FCL]: 'Total container quantity & type',
  [CargoModeEnum.LOOSE]: 'Total cargo quantity & type',
  [CargoModeEnum.FTL]: 'Vehicle size & type'
};

export const cargoLabelMapping: Record<CargoModeEnum, string> = {
  [CargoModeEnum.FCL]: 'Container quantity & type',
  [CargoModeEnum.LOOSE]: 'Cargo quantity & type',
  [CargoModeEnum.FTL]: 'Vehicle size & type'
};

const coreCargoContainerTypes: ContainerType[] = [
  {
    label: coreCargoContainerTypeLabelMapping[cargoValues.containerX20Dv].long,
    type: cargoValues.containerX20Dv
  },
  {
    label: coreCargoContainerTypeLabelMapping[cargoValues.containerX20Hc].long,
    type: cargoValues.containerX20Hc
  },
  {
    label: coreCargoContainerTypeLabelMapping[cargoValues.containerX40Dv].long,
    type: cargoValues.containerX40Dv
  },
  {
    label: coreCargoContainerTypeLabelMapping[cargoValues.containerX40Hc].long,
    type: cargoValues.containerX40Hc
  },
  {
    label: coreCargoContainerTypeLabelMapping[cargoValues.containerX45Hc].long,
    type: cargoValues.containerX45Hc
  }
];

export const containerOptions: Option<CoreCargoType>[] = coreCargoContainerTypes.map(
  ({ type, label }: ContainerType): { label: string; value: CoreCargoType } => ({
    value: type,
    label
  })
);

export const cargoModeOptions: Option<CargoModeEnum>[] = [
  { value: CargoModeEnum.FCL, label: cargoModeLabelMapping[CargoModeEnum.FCL] },
  { value: CargoModeEnum.FTL, label: cargoModeLabelMapping[CargoModeEnum.FTL] },
  { value: CargoModeEnum.LOOSE, label: cargoModeLabelMapping[CargoModeEnum.LOOSE] }
];

export const looseCargoOptions: Option<LooseCargoTypeEnum>[] = [
  {
    label: cargoTypeLabelMapping[LooseCargoTypeEnum.ASSORTED_CARGO],
    value: LooseCargoTypeEnum.ASSORTED_CARGO
  },
  {
    label: cargoTypeLabelMapping[LooseCargoTypeEnum.BOXES_OR_CRATES],
    value: LooseCargoTypeEnum.BOXES_OR_CRATES
  },
  {
    label: cargoTypeLabelMapping[LooseCargoTypeEnum.PALLETS],
    value: LooseCargoTypeEnum.PALLETS
  }
];

export const looseCoreCargoOptions: Option<CoreCargoType>[] = [
  {
    label: coreCargoTypeLabelMapping[cargoValues.looseAssortedCargo],
    value: cargoValues.looseAssortedCargo
  },
  {
    label: coreCargoTypeLabelMapping[cargoValues.looseBoxesOrCrates],
    value: cargoValues.looseBoxesOrCrates
  },
  {
    label: coreCargoTypeLabelMapping[cargoValues.loosePalletLegacy],
    value: cargoValues.loosePalletLegacy
  }
];

export const vehicleOptions: Option<VehicleTypeEnum>[] = [
  {
    label: cargoTypeLabelMapping[VehicleTypeEnum.TRAILER_13_6_M_BOX],
    value: VehicleTypeEnum.TRAILER_13_6_M_BOX
  },
  {
    label: cargoTypeLabelMapping[VehicleTypeEnum.TRAILER_13_6_M_CURTAIN_SIDED],
    value: VehicleTypeEnum.TRAILER_13_6_M_CURTAIN_SIDED
  },
  {
    label: cargoTypeLabelMapping[VehicleTypeEnum.TRAILER_13_6_M_MEGATRAILER],
    value: VehicleTypeEnum.TRAILER_13_6_M_MEGATRAILER
  },
  {
    label: cargoTypeLabelMapping[VehicleTypeEnum.TRAILER_13_6_M_SLIDING_ROOF],
    value: VehicleTypeEnum.TRAILER_13_6_M_SLIDING_ROOF
  },
  {
    label: cargoTypeLabelMapping[VehicleTypeEnum.TRAILER_18_T_BOX],
    value: VehicleTypeEnum.TRAILER_18_T_BOX
  },
  {
    label: cargoTypeLabelMapping[VehicleTypeEnum.TRAILER_18_T_CURTAIN_SIDED],
    value: VehicleTypeEnum.TRAILER_18_T_CURTAIN_SIDED
  },
  {
    label: cargoTypeLabelMapping[VehicleTypeEnum.TRAILER_18_T_SLIDING_ROOF],
    value: VehicleTypeEnum.TRAILER_18_T_SLIDING_ROOF
  },
  {
    label: cargoTypeLabelMapping[VehicleTypeEnum.TRAILER_7_5_T_BOX],
    value: VehicleTypeEnum.TRAILER_7_5_T_BOX
  },
  {
    label: cargoTypeLabelMapping[VehicleTypeEnum.TRAILER_7_5_T_CURTAIN_SIDED],
    value: VehicleTypeEnum.TRAILER_7_5_T_CURTAIN_SIDED
  },
  {
    label: cargoTypeLabelMapping[VehicleTypeEnum.TRAILER_7_5_T_SLIDING_ROOF],
    value: VehicleTypeEnum.TRAILER_7_5_T_SLIDING_ROOF
  },
  {
    label: cargoTypeLabelMapping[VehicleTypeEnum.VAN_BOX],
    value: VehicleTypeEnum.VAN_BOX
  },
  {
    label: cargoTypeLabelMapping[VehicleTypeEnum.VAN_CURTAIN_SIDED],
    value: VehicleTypeEnum.VAN_CURTAIN_SIDED
  },
  {
    label: cargoTypeLabelMapping[VehicleTypeEnum.VAN_SLIDING_ROOF],
    value: VehicleTypeEnum.VAN_SLIDING_ROOF
  }
];

export const coreCargoVehicleOptions: Option<CoreCargoType>[] = [
  {
    label: coreCargoTypeLabelMapping[cargoValues.trailer136MBox],
    value: cargoValues.trailer136MBox
  },
  {
    label: coreCargoTypeLabelMapping[cargoValues.trailer136MCurtainSided],
    value: cargoValues.trailer136MCurtainSided
  },
  {
    label: coreCargoTypeLabelMapping[cargoValues.trailer136MMegatrailer],
    value: cargoValues.trailer136MMegatrailer
  },
  {
    label: coreCargoTypeLabelMapping[cargoValues.trailer136MSlidingRoof],
    value: cargoValues.trailer136MSlidingRoof
  },
  {
    label: coreCargoTypeLabelMapping[cargoValues.trailer18TBox],
    value: cargoValues.trailer18TBox
  },
  {
    label: coreCargoTypeLabelMapping[cargoValues.trailer18TCurtainSided],
    value: cargoValues.trailer18TCurtainSided
  },
  {
    label: coreCargoTypeLabelMapping[cargoValues.trailer18TSlidingRoof],
    value: cargoValues.trailer18TSlidingRoof
  },
  {
    label: coreCargoTypeLabelMapping[cargoValues.trailer75TBox],
    value: cargoValues.trailer75TBox
  },
  {
    label: coreCargoTypeLabelMapping[cargoValues.trailer75TCurtainSided],
    value: cargoValues.trailer75TCurtainSided
  },
  {
    label: coreCargoTypeLabelMapping[cargoValues.trailerVanBox],
    value: cargoValues.trailerVanBox
  },
  {
    label: coreCargoTypeLabelMapping[cargoValues.trailerVanCurtainSided],
    value: cargoValues.trailerVanCurtainSided
  },
  {
    label: coreCargoTypeLabelMapping[cargoValues.straightTruck],
    value: cargoValues.straightTruck
  },
  {
    label: coreCargoTypeLabelMapping[cargoValues.trailer136MHiab],
    value: cargoValues.trailer136MHiab
  },
  {
    label: coreCargoTypeLabelMapping[cargoValues.trailer136MMegatrailerCurtainSided],
    value: cargoValues.trailer136MMegatrailerCurtainSided
  },
  {
    label: coreCargoTypeLabelMapping[cargoValues.trailer136MRefrigerated],
    value: cargoValues.trailer136MRefrigerated
  },
  {
    label: coreCargoTypeLabelMapping[cargoValues.trailer18THiab],
    value: cargoValues.trailer18THiab
  },
  {
    label: coreCargoTypeLabelMapping[cargoValues.trailer18TRefrigerate],
    value: cargoValues.trailer18TRefrigerate
  },
  {
    label: coreCargoTypeLabelMapping[cargoValues['35TBox']],
    value: cargoValues['35TBox']
  },
  {
    label: coreCargoTypeLabelMapping[cargoValues['35TCurtainSided']],
    value: cargoValues['35TCurtainSided']
  },
  {
    label: coreCargoTypeLabelMapping[cargoValues['53FtTrailer']],
    value: cargoValues['53FtTrailer']
  }
];

export const maxCargoHeightForRoadShipment: Record<CargoDimensionsUnitEnum, number> = {
  [CargoDimensionsUnitEnum.CM]: 265,
  [CargoDimensionsUnitEnum.MM]: 2650,
  [CargoDimensionsUnitEnum.IN]: 104.33
};

export const isValidHeightForRoadShipment = (
  height: Optional<number>,
  dimensionsUnit: CargoDimensionsUnitEnum,
  modeOfTransport: Optional<ModeOfTransport>
): boolean => {
  if (!height || modeOfTransport !== ModeOfTransport.TRUCK) {
    return true;
  }

  return height <= maxCargoHeightForRoadShipment[dimensionsUnit];
};

export const isTemplatedPalletCoreCargoType = (palletType: Optional<CoreCargoType>): boolean => {
  if (!palletType) {
    return false;
  }

  return templatedPalletTypes.includes(palletType);
};

export const getDefaultCoreCargoGrossWeight = (palletType: Optional<CoreCargoType>): DeepNullable<CargoWeight> => {
  const unit: CargoWeightUnitEnum = palletType === cargoValues.loosePalletUs ? CargoWeightUnitEnum.LBS : CargoWeightUnitEnum.KG;

  return {
    value: null,
    unit
  };
};

export const getCoreCargoTypeLabel = (cargoType: Optional<CoreCargoType>): string => {
  return cargoType ? coreCargoTypeLabelMapping[cargoType] : '';
};

export const getLooseCargoTypeWithPallet = (cargoType: CoreCargoType): CoreCargoType => {
  return isCoreCargoPalletType(cargoType) ? cargoValues.loosePalletLegacy : cargoType;
};
